/**
 * /help and /help/<topic>: the written reference the tours point to. Topics are
 * gated by the same access matrix as the pages they describe; each one can open
 * its page and run that page's tour.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, Container, Divider, InputAdornment, List, ListItemButton, ListItemText, ListSubheader,
  Paper, TextField, Typography, useMediaQuery, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SideMenu from '../../components/SideMenu';
import { useUser } from '../../context/UserContext';
import { useOrg } from '../../context/OrgContext';
import { settingsAccess } from '../../utils/settingsAccess';
import { topicsFor, AUDIENCE_LABEL } from '../../help/topics';
import { usePageTour } from '../../tours/usePageTour';
import { queueTour } from '../../tours/TourProvider';

const fill = (text, company) => (text || '').replace(/\{\{company\}\}/g, company);

export default function Help() {
  const navigate = useNavigate();
  const { '*': splat } = useParams();
  const isSmallScreen = useMediaQuery('(max-width:900px)');
  const { currentUser } = useUser();
  const { name: companyName } = useOrg();
  const access = useMemo(() => settingsAccess(currentUser), [currentUser]);
  const topics = useMemo(() => topicsFor(access), [access]);
  const [query, setQuery] = useState('');

  usePageTour('help', !!currentUser);

  const activeKey = topics.some((t) => t.key === splat) ? splat : topics[0]?.key;
  const topic = topics.find((t) => t.key === activeKey);
  useEffect(() => {
    if (activeKey && splat !== activeKey) navigate(`/help/${activeKey}`, { replace: true });
  }, [splat, activeKey, navigate]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter((t) => [t.title, t.summary, ...t.steps.map((s) => `${s.heading} ${s.text}`)].join(' ').toLowerCase().includes(q));
  }, [topics, query]);

  const groups = useMemo(() => {
    const order = ['all', 'management', 'provider', 'owner', 'platform'];
    return order.map((a) => ({ audience: a, items: shown.filter((t) => t.audience === a) })).filter((g) => g.items.length);
  }, [shown]);

  const runTour = () => {
    if (!topic) return;
    if (topic.tour) queueTour(topic.tour);
    navigate(topic.route);
  };

  const nav = isSmallScreen ? (
    <FormControl fullWidth size="small" sx={{ mb: 2 }} data-tour="help-nav">
      <InputLabel>Topic</InputLabel>
      <Select label="Topic" value={activeKey || ''} onChange={(e) => navigate(`/help/${e.target.value}`)}>
        {groups.flatMap((g) => [
          <ListSubheader key={`h-${g.audience}`}>{AUDIENCE_LABEL[g.audience]}</ListSubheader>,
          ...g.items.map((t) => <MenuItem key={t.key} value={t.key}>{t.title}</MenuItem>)
        ])}
      </Select>
    </FormControl>
  ) : (
    <Paper variant="outlined" sx={{ width: 300, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 16, '@media print': { display: 'none' } }} data-tour="help-nav">
      <Box sx={{ p: 1.5 }}>
        <TextField size="small" fullWidth placeholder="Search help" value={query} onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Box>
      <List dense disablePadding sx={{ maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }}>
        {groups.map((g) => (
          <React.Fragment key={g.audience}>
            <ListSubheader disableSticky sx={{ lineHeight: '36px', fontWeight: 700 }}>{AUDIENCE_LABEL[g.audience]}</ListSubheader>
            {g.items.map((t) => (
              <ListItemButton key={t.key} selected={t.key === activeKey} onClick={() => navigate(`/help/${t.key}`)} sx={{ pl: 3 }}>
                <ListItemText primary={t.title} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
            ))}
          </React.Fragment>
        ))}
        {!groups.length && <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Nothing matches "{query}".</Typography>}
      </List>
    </Paper>
  );

  return (
    <>
      <Box sx={{ '@media print': { display: 'none' } }}><SideMenu /></Box>
      <Container maxWidth={false}>
        <Box mt={4} mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h4">Help &amp; tutorials</Typography>
            <Typography variant="body2" color="text.secondary">How each part of the {companyName} portal works, step by step. Every page also has a guided tour: press ? at the top of any page.</Typography>
          </Box>
          <Button size="small" startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ '@media print': { display: 'none' } }}>Print</Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: isSmallScreen ? 'column' : 'row' }}>
          {nav}
          <Box sx={{ flex: 1, minWidth: 0 }} data-tour="help-topic">
            {topic ? (
              <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, maxWidth: 860 }}>
                <Chip size="small" label={AUDIENCE_LABEL[topic.audience]} sx={{ mb: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{fill(topic.title, companyName)}</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{fill(topic.summary, companyName)}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
                  {topic.route && (
                    <Button size="small" variant="contained" startIcon={topic.tour ? <PlayCircleOutlineIcon /> : <OpenInNewIcon />} onClick={runTour}>
                      {topic.tour ? 'Open the page and run its tour' : 'Open the page'}
                    </Button>
                  )}
                </Box>
                <Divider sx={{ my: 2.5 }} />
                <Box component="ol" sx={{ pl: 3, m: 0 }}>
                  {topic.steps.map((s, i) => (
                    <Box component="li" key={i} sx={{ mb: 2.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{fill(s.heading, companyName)}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{fill(s.text, companyName)}</Typography>
                      {s.tip && <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>Tip: {fill(s.tip, companyName)}</Typography>}
                    </Box>
                  ))}
                </Box>
                {topic.related?.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="overline" color="text.secondary">Related</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      {topic.related.map((k) => topics.find((t) => t.key === k)).filter(Boolean).map((t) => (
                        <Chip key={t.key} label={t.title} size="small" variant="outlined" onClick={() => navigate(`/help/${t.key}`)} />
                      ))}
                    </Box>
                  </>
                )}
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">No help topics are available for your role yet.</Typography>
            )}
          </Box>
        </Box>
      </Container>
    </>
  );
}
